// DAO for mbank_payment_links. The link table is append-only in the
// happy path — refunds and reversals create new rows rather than
// mutating existing ones.

import type { PostgrestError } from '@supabase/supabase-js';
import { BaseDao, type InvalidateFn, type SupabaseClient } from '../../base.ts';
import type {
  MbankFakturowniaPushStatus,
  MbankPaymentLinkInsert,
  MbankPaymentLinkRow,
  MbankPaymentLinkType,
} from '@shared/types/mbank.ts';

// PostgREST silently truncates a response at ~1000 rows.
const POSTGREST_RESPONSE_CAP = 1000;

export class MbankPaymentLinkDao extends BaseDao<MbankPaymentLinkRow> {
  protected readonly dependentResources = ['mbank_payment_links', 'mbank_invoice_payment_state'];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'mbank_payment_links', invalidate);
  }

  /**
   * Create a link, idempotent on (statement_line_id, invoice_id, link_type).
   * Returns the resulting row whether newly inserted or pre-existing.
   */
  async createIdempotent(input: MbankPaymentLinkInsert): Promise<{
    row: MbankPaymentLinkRow;
    wasInserted: boolean;
  }> {
    // Probe for an existing row first.
    const { data: existing, error: qErr } = await this.sb
      .from('mbank_payment_links')
      .select('*')
      .eq('statement_line_id', input.statement_line_id)
      .eq('invoice_id', input.invoice_id)
      .eq('link_type', input.link_type)
      .maybeSingle<MbankPaymentLinkRow>();
    if (qErr) throw qErr;
    if (existing) {
      return { row: existing, wasInserted: false };
    }

    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .insert(input)
      .select('*')
      .single<MbankPaymentLinkRow>();
    if (error) throw error;
    this.handleInvalidation();
    return { row: data, wasInserted: true };
  }

  /** All links for a given statement line. */
  async findByStatementLine(statementLineId: number): Promise<MbankPaymentLinkRow[]> {
    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .select('*')
      .eq('statement_line_id', statementLineId);
    if (error) throw error;
    return (data ?? []) as MbankPaymentLinkRow[];
  }

  /**
   * Links for many statement lines at once (finding fa_import_echo — the
   * FA-import pairing pass batches L-link lookups across payments). Chunked
   * `.in()` at 200; empty input short-circuits without a query.
   */
  async findByStatementLineIds(ids: number[]): Promise<MbankPaymentLinkRow[]> {
    if (ids.length === 0) return [];
    const out: MbankPaymentLinkRow[] = [];
    for (let i = 0; i < ids.length; i += 200) {
      const chunk = ids.slice(i, i + 200);
      const { data, error } = await this.sb
        .from('mbank_payment_links')
        .select('*')
        .in('statement_line_id', chunk);
      if (error) throw error;
      out.push(...((data ?? []) as MbankPaymentLinkRow[]));
    }
    return out;
  }

  /**
   * Links echoing the given Fakturownia payments (interim pairing's mbank_push
   * branch: a push-originated FA payment pairs to its own link's statement line).
   * Chunked `.in()` at 200 like {@link findByStatementLineIds}; empty input
   * short-circuits without a query.
   */
  async findByFakturowniaPaymentIds(ids: string[]): Promise<MbankPaymentLinkRow[]> {
    if (ids.length === 0) return [];
    const out: MbankPaymentLinkRow[] = [];
    for (let i = 0; i < ids.length; i += 200) {
      const chunk = ids.slice(i, i + 200);
      const { data, error } = await this.sb
        .from('mbank_payment_links')
        .select('*')
        .in('fakturownia_payment_id', chunk);
      if (error) throw error;
      out.push(...((data ?? []) as MbankPaymentLinkRow[]));
    }
    return out;
  }

  /** All links for a given invoice. */
  async findByInvoice(invoiceId: number): Promise<MbankPaymentLinkRow[]> {
    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as MbankPaymentLinkRow[];
  }

  /**
   * Sum applied_in_invoice_ccy per invoice_id across the given ids, reversal-aware:
   * a link with role='reversal' REDUCES coverage, everything else increases it. The
   * returned magnitudes are keyed by the row's own invoice_id (the caller folds
   * corrections onto their parent). Invoices with no links are absent from the map.
   *
   * Extracted here (finding MINOR-8) so the mbank-send-payment edge fn and
   * MbankManualMatchService.assertSplitsWithinOutstanding stop hand-writing the
   * same `mbank_payment_links` aggregation query.
   */
  async sumAppliedByInvoice(invoiceIds: number[]): Promise<Map<number, number>> {
    const byInvoice = new Map<number, number>();
    if (invoiceIds.length === 0) return byInvoice;
    type Slim = { invoice_id: number; applied_in_invoice_ccy: number | null; role: string | null };
    // A response of this many rows may have been truncated, so it is never summed.
    const readChunk = async (ids: number[]): Promise<void> => {
      const { data, error } = await this.sb
        .from('mbank_payment_links')
        .select('invoice_id, applied_in_invoice_ccy, role')
        .in('invoice_id', ids);
      if (error) throw error;
      const rows = (data ?? []) as Slim[];
      if (rows.length >= POSTGREST_RESPONSE_CAP) {
        if (ids.length === 1) {
          throw new Error(
            `invoice ${ids[0]} has more than ${POSTGREST_RESPONSE_CAP} bank links on one invoice — refusing a truncated sum`,
          );
        }
        const mid = Math.ceil(ids.length / 2);
        await readChunk(ids.slice(0, mid));
        await readChunk(ids.slice(mid));
        return;
      }
      for (const l of rows) {
        const sign = l.role === 'reversal' ? -1 : 1;
        byInvoice.set(
          l.invoice_id,
          (byInvoice.get(l.invoice_id) ?? 0) + sign * Math.abs(Number(l.applied_in_invoice_ccy ?? 0)),
        );
      }
    };
    for (let i = 0; i < invoiceIds.length; i += 100) {
      await readChunk(invoiceIds.slice(i, i + 100));
    }
    return byInvoice;
  }

  /** Pending Fakturownia-push queue. Optional `invoiceIds` scopes the queue
   *  to selected invoices (sync-review "send now").
   *
   *  @deprecated Reads without claiming — allows two workers to push the same
   *  row (at-least-once). Use {@link claimPendingPush}, which flips rows to
   *  'pushing' under FOR UPDATE SKIP LOCKED. Kept only for read-only previews. */
  async findPendingPush(limit = 100, invoiceIds?: number[]): Promise<MbankPaymentLinkRow[]> {
    let query = this.sb
      .from('mbank_payment_links')
      .select('*')
      .eq('fakturownia_push_status', 'pending');
    if (invoiceIds && invoiceIds.length > 0) query = query.in('invoice_id', invoiceIds);
    const { data, error } = await query
      .order('created_at', { ascending: true })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as MbankPaymentLinkRow[];
  }

  /**
   * Atomically claim rows for a Fakturownia /payments push (findings A6/A7/m1).
   *
   * Flips eligible rows to 'pushing' under FOR UPDATE SKIP LOCKED via the
   * `claim_mbank_payment_pushes` RPC, so concurrent workers (workflow, cron
   * edge fn, sync-review "send now") never grab — and double-POST — the same row.
   *
   * Eligible rows: 'pending', 'unknown' (ambiguous), stale 'pushing' (crashed
   * worker), and retry-eligible 'failed' (attempts < maxAttempts and last
   * attempt older than the backoff window).
   */
  async claimPendingPush(opts: {
    limit: number;
    maxAttempts: number;
    reclaimMinutes: number;
    backoffMinutes: number;
    invoiceIds?: number[];
  }): Promise<MbankPaymentLinkRow[]> {
    // The client is schema-untyped, so the RPC response is `any` — narrow it
    // here rather than letting `any` leak into the caller.
    const { data, error } = (await this.sb.rpc('claim_mbank_payment_pushes', {
      p_limit: opts.limit,
      p_max_attempts: opts.maxAttempts,
      p_reclaim_minutes: opts.reclaimMinutes,
      p_backoff_minutes: opts.backoffMinutes,
      p_invoice_ids: opts.invoiceIds && opts.invoiceIds.length > 0 ? opts.invoiceIds : null,
    })) as { data: MbankPaymentLinkRow[] | null; error: PostgrestError | null };
    if (error) throw error;
    this.handleInvalidation();
    return data ?? [];
  }

  /**
   * Resolve invoices.external_system_id (Fakturownia's invoice id) for a set of
   * local invoice ids (finding A5). mbank_payment_links.invoice_id is a LOCAL FK;
   * the /payments POST needs Fakturownia's id. Rows whose invoice has no external
   * id come back missing from the map so the caller can park them (never POST).
   */
  async findExternalIdsForInvoices(invoiceIds: number[]): Promise<Map<number, string>> {
    const out = new Map<number, string>();
    if (invoiceIds.length === 0) return out;
    const { data, error } = await this.sb
      .from('invoices')
      .select('id, external_system_id')
      .in('id', invoiceIds);
    if (error) throw error;
    for (const row of (data ?? []) as { id: number; external_system_id: string | null }[]) {
      if (row.external_system_id) out.set(row.id, row.external_system_id);
    }
    return out;
  }

  /**
   * Which of the given invoices have ANY payment links (finding m4). For those,
   * bank-derived truth reaches Fakturownia as /payments rows and FA derives the
   * payment status itself — the outbound dirty-flush must NOT also PUT
   * payment_status or the two writers churn against each other.
   */
  async findInvoiceIdsWithLinks(invoiceIds: number[]): Promise<Set<number>> {
    if (invoiceIds.length === 0) return new Set();
    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .select('invoice_id')
      .in('invoice_id', invoiceIds);
    if (error) throw error;
    return new Set(((data ?? []) as { invoice_id: number }[]).map(r => r.invoice_id));
  }

  async setPushStatus(
    id: number,
    status: MbankFakturowniaPushStatus,
    extras: {
      fakturownia_payment_id?: string | null;
      fakturownia_push_error?: string | null;
      fakturownia_pushed_at?: string | null;
      incrementAttempts?: boolean;
    } = {},
  ): Promise<void> {
    const update: Record<string, unknown> = {
      fakturownia_push_status: status,
      // Only touch fakturownia_payment_id when the caller supplied the key — a
      // status-only update (e.g. marking 'failed') must not wipe an id set by an
      // earlier successful push.
      ...('fakturownia_payment_id' in extras
        ? { fakturownia_payment_id: extras.fakturownia_payment_id ?? null }
        : {}),
      fakturownia_push_error: extras.fakturownia_push_error ?? null,
      // Release the claim on any terminal / retry-able state; stamp the attempt
      // so failed-row backoff (finding m1) has a reference point.
      push_claimed_at: null,
      last_attempt_at: new Date().toISOString(),
    };
    if (extras.fakturownia_pushed_at !== undefined) {
      update.fakturownia_pushed_at = extras.fakturownia_pushed_at;
    }
    const { data: prev, error: qErr } = await this.sb
      .from('mbank_payment_links')
      .select('fakturownia_push_attempts')
      .eq('id', id)
      .single();
    if (qErr) throw qErr;
    if (extras.incrementAttempts) {
      update.fakturownia_push_attempts =
        ((prev as { fakturownia_push_attempts: number } | null)?.fakturownia_push_attempts ?? 0) + 1;
    }
    const { error } = await this.sb
      .from('mbank_payment_links')
      .update(update)
      .eq('id', id);
    if (error) throw error;
    this.handleInvalidation();
  }

  /**
   * Reset one failed push back to 'pending' so the next
   * fakturownia-bidi-sync /push-payments run picks it up again. Clears
   * the error message and the push_attempts counter — the retry starts
   * with a clean slate. No-op when the link isn't in 'failed' state.
   */
  async resetFailedPush(linkId: number): Promise<boolean> {
    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .update({
        fakturownia_push_status: 'pending',
        fakturownia_push_error: null,
        fakturownia_push_attempts: 0,
        push_claimed_at: null,
      })
      .eq('id', linkId)
      .eq('fakturownia_push_status', 'failed')
      .select('id');
    if (error) throw error;
    this.handleInvalidation();
    return (data?.length ?? 0) > 0;
  }

  /** Reset every failed push back to pending. Returns the updated count. */
  async resetAllFailedPushes(): Promise<number> {
    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .update({
        fakturownia_push_status: 'pending',
        fakturownia_push_error: null,
        fakturownia_push_attempts: 0,
        push_claimed_at: null,
      })
      .eq('fakturownia_push_status', 'failed')
      .select('id');
    if (error) throw error;
    this.handleInvalidation();
    return data?.length ?? 0;
  }

  /**
   * Find an existing link with the opposite sign that matches the given
   * line on counterparty + amount within a window — used by the matcher
   * to detect an implicit reversal that the bank didn't flag with RvslInd.
   */
  async findPriorLinkForImplicitReversal(params: {
    invoiceId: number;
    counterpartyIban: string | null;
    amount: number;
    currency: string;
    windowDays: number;
    asOfDate: string; // YYYY-MM-DD
  }): Promise<MbankPaymentLinkRow | null> {
    const windowStart = new Date(params.asOfDate);
    windowStart.setUTCDate(windowStart.getUTCDate() - params.windowDays);
    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .select('*')
      .eq('invoice_id', params.invoiceId)
      .eq('applied_currency', params.currency)
      .eq('link_type', 'payment')
      .gte('created_at', windowStart.toISOString())
      .order('created_at', { ascending: false });
    if (error) throw error;
    const rows = (data ?? []) as MbankPaymentLinkRow[];
    for (const row of rows) {
      if (Math.abs(Math.abs(row.applied_amount) - Math.abs(params.amount)) <= 0.01) {
        return row;
      }
    }
    return null;
  }

  /**
   * Load links by primary key. Used by payments echo-healing: a Fakturownia
   * payment carrying our `mbank:<link_id>` dedupe token is resolved back to the
   * originating link so its push status can be reconciled. Empty input
   * short-circuits; ids with no row are simply absent from the result.
   */
  async findByIds(ids: number[]): Promise<MbankPaymentLinkRow[]> {
    if (ids.length === 0) return [];
    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .select('*')
      .in('id', ids);
    if (error) throw error;
    return (data ?? []) as MbankPaymentLinkRow[];
  }

  /**
   * Bank links on the given invoices that have NOT yet been adopted by a
   * Fakturownia payment: `fakturownia_payment_id IS NULL` AND push status is
   * neither 'pushed' nor 'pushing' — a 'pushing' row is CLAIMED by a concurrent
   * pusher worker mid-POST; adopting it here would race that POST and risk a
   * double payment. The payments reconciler uses these as candidates to explain
   * an external FA payment (allocation → 'linked'), then marks the winning link
   * 'pushed' with the payment id. Empty input short-circuits; ordered
   * oldest-first so the reconciler's earliest-wins tie-break is stable.
   */
  async findUnadoptedByInvoices(invoiceIds: number[]): Promise<MbankPaymentLinkRow[]> {
    if (invoiceIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('mbank_payment_links')
      .select('*')
      .in('invoice_id', invoiceIds)
      .is('fakturownia_payment_id', null)
      .neq('fakturownia_push_status', 'pushed')
      .neq('fakturownia_push_status', 'pushing')
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as MbankPaymentLinkRow[];
  }

  /** Every link in the table, keyset-paged on id (a plain read is cut at the
   *  PostgREST response cap). Only the columns the over-link repair judges. */
  async listAllForRepair(): Promise<OverlinkRepairLink[]> {
    const out: OverlinkRepairLink[] = [];
    let after = 0;
    for (;;) {
      const { data, error } = await this.sb
        .from('mbank_payment_links')
        .select('id, invoice_id, statement_line_id, role, match_rule, applied_in_invoice_ccy')
        .gt('id', after)
        .order('id', { ascending: true })
        .limit(POSTGREST_RESPONSE_CAP);
      if (error) throw error;
      const rows = (data ?? []) as OverlinkRepairLink[];
      out.push(...rows);
      if (rows.length < POSTGREST_RESPONSE_CAP) return out;
      after = rows[rows.length - 1].id;
    }
  }

  /** Live invoices of the given companies (either side), with the fields the
   *  title repair judges. Chunked by company, keyset-paged on id. */
  async findInvoicesOfCompaniesForRepair(companyIds: number[]): Promise<TitleRepairInvoice[]> {
    const out = new Map<number, TitleRepairInvoice>();
    for (let i = 0; i < companyIds.length; i += 100) {
      const chunk = companyIds.slice(i, i + 100);
      for (const side of ['seller_company_id', 'buyer_company_id'] as const) {
        for (let after = 0; ; ) {
          const { data, error } = await this.sb
            .from('invoices')
            .select('id, invoice_number, issue_date, seller_company_id, buyer_company_id, gross_amount')
            .in(side, chunk)
            .eq('is_deleted', false)
            .gt('id', after)
            .order('id', { ascending: true })
            .limit(POSTGREST_RESPONSE_CAP);
          if (error) throw error;
          const rows = (data ?? []) as TitleRepairInvoice[];
          for (const r of rows) out.set(r.id, r);
          if (rows.length < POSTGREST_RESPONSE_CAP) break;
          after = rows[rows.length - 1].id;
        }
      }
    }
    return [...out.values()];
  }

  /** Invoice number of the given invoices, by id (chunks of 200). */
  async findInvoiceNumbers(invoiceIds: number[]): Promise<Map<number, string | null>> {
    const out = new Map<number, string | null>();
    for (let i = 0; i < invoiceIds.length; i += 200) {
      const { data, error } = await this.sb
        .from('invoices')
        .select('id, invoice_number')
        .in('id', invoiceIds.slice(i, i + 200));
      if (error) throw error;
      for (const r of (data ?? []) as Array<{ id: number; invoice_number: string | null }>) out.set(r.id, r.invoice_number);
    }
    return out;
  }

  /** Issue date of the given invoices, by id (chunks of 200). */
  async findIssueDates(invoiceIds: number[]): Promise<Map<number, string | null>> {
    const out = new Map<number, string | null>();
    for (let i = 0; i < invoiceIds.length; i += 200) {
      const { data, error } = await this.sb
        .from('invoices')
        .select('id, issue_date')
        .in('id', invoiceIds.slice(i, i + 200));
      if (error) throw error;
      for (const r of (data ?? []) as Array<{ id: number; issue_date: string | null }>) out.set(r.id, r.issue_date);
    }
    return out;
  }

  /** Direction ('credit' | 'debit') of the given statement lines, by line id.
   *  Chunks of 200 unique ids can never reach the response cap, so no paging. */
  async findLineDirectionsForRepair(lineIds: number[]): Promise<Map<number, string>> {
    const out = new Map<number, string>();
    for (let i = 0; i < lineIds.length; i += 200) {
      const { data, error } = await this.sb
        .from('mbank_statement_line')
        .select('id, direction')
        .in('id', lineIds.slice(i, i + 200));
      if (error) throw error;
      for (const r of (data ?? []) as Array<{ id: number; direction: string | null }>) {
        if (r.direction) out.set(r.id, r.direction);
      }
    }
    return out;
  }

  /** Gross facts of the given invoices (chunked `.in()` at 200). */
  async findInvoiceGrossFacts(invoiceIds: number[]): Promise<OverlinkRepairInvoice[]> {
    const out: OverlinkRepairInvoice[] = [];
    for (let i = 0; i < invoiceIds.length; i += 200) {
      const { data, error } = await this.sb
        .from('invoices')
        .select('id, gross_amount, invoice_type, correction_of_invoice_id, is_deleted')
        .in('id', invoiceIds.slice(i, i + 200));
      if (error) throw error;
      out.push(...((data ?? []) as OverlinkRepairInvoice[]));
    }
    return out;
  }

  /** Live (non-deleted) corrections of the given parent invoices, keyset-paged
   *  on id inside each chunk of 200 parents. */
  async findLiveCorrectionGrossByParent(parentIds: number[]): Promise<OverlinkRepairCorrection[]> {
    const out: OverlinkRepairCorrection[] = [];
    for (let i = 0; i < parentIds.length; i += 200) {
      const chunk = parentIds.slice(i, i + 200);
      let after = 0;
      for (;;) {
        const { data, error } = await this.sb
          .from('invoices')
          .select('id, correction_of_invoice_id, gross_amount')
          .in('correction_of_invoice_id', chunk)
          .eq('is_deleted', false)
          .gt('id', after)
          .order('id', { ascending: true })
          .limit(POSTGREST_RESPONSE_CAP);
        if (error) throw error;
        const rows = (data ?? []) as OverlinkRepairCorrection[];
        out.push(...rows);
        if (rows.length < POSTGREST_RESPONSE_CAP) break;
        after = rows[rows.length - 1].id;
      }
    }
    return out;
  }

  /** External Fakturownia payment ids whose allocations point at any of the
   *  given links (chunked `.in()` at 200, keyset-paged on id inside a chunk). */
  async findFaPaymentIdsAllocatedToLinks(linkIds: number[]): Promise<string[]> {
    const out = new Set<string>();
    for (let i = 0; i < linkIds.length; i += 200) {
      const chunk = linkIds.slice(i, i + 200);
      let after = 0;
      for (;;) {
        const { data, error } = await this.sb
          .from('fakturownia_payment_allocation')
          .select('id, external_payment_id')
          .in('mbank_payment_link_id', chunk)
          .gt('id', after)
          .order('id', { ascending: true })
          .limit(POSTGREST_RESPONSE_CAP);
        if (error) throw error;
        const rows = (data ?? []) as { id: number; external_payment_id: string }[];
        for (const r of rows) out.add(r.external_payment_id);
        if (rows.length < POSTGREST_RESPONSE_CAP) break;
        after = rows[rows.length - 1].id;
      }
    }
    return [...out];
  }

  /** Delete links by id (chunked at 200). The recompute trigger refreshes the
   *  owning invoices' payment status. */
  async deleteByIds(ids: number[]): Promise<void> {
    for (let i = 0; i < ids.length; i += 200) {
      const { error } = await this.sb
        .from('mbank_payment_links')
        .delete()
        .in('id', ids.slice(i, i + 200));
      if (error) throw error;
    }
    if (ids.length > 0) this.handleInvalidation();
  }
}

/** Link columns the over-link repair reads. */
export interface OverlinkRepairLink {
  id: number;
  invoice_id: number;
  statement_line_id: number;
  role: string | null;
  match_rule: string | null;
  applied_in_invoice_ccy: number | null;
}

export interface OverlinkRepairInvoice {
  id: number;
  gross_amount: number | null;
  invoice_type: string | null;
  correction_of_invoice_id: number | null;
  is_deleted: boolean;
}

export interface OverlinkRepairCorrection {
  id: number;
  correction_of_invoice_id: number;
  gross_amount: number | null;
}

// ── Signed-sum helper (pure) ──────────────────────────────────────────────

export type MbankLinkSignDirection = 'credit' | 'debit';

/**
 * Convert a statement-line amount + direction + link_type into the signed
 * value that goes into mbank_payment_links.applied_amount.
 *
 * Invariant: invoice paid state = SUM(applied_amount) across all links.
 * - A normal credit payment on a revenue invoice is positive.
 * - A reversal of that credit is negative.
 * - A normal debit payment of a cost invoice is negative (we sent money).
 * - A reversal of that debit is positive.
 *
 * Revenue vs cost direction lives on the invoice; this helper only cares
 * about the entry direction + whether it's a forward or reverse event.
 */
// Pure money helper (no Supabase access), mirrored by the recompute_invoice_payment_status DB trigger and
// imported directly by src/entities/invoices/*. Hoisting it into the DAO class would put a stateless
// function behind an instance and churn out-of-scope call sites.
// eslint-disable-next-line no-restricted-syntax -- pure helper, not data access; see note above
export function signedLinkAmount(
  entryAmount: number,
  entryDirection: MbankLinkSignDirection,
  linkType: MbankPaymentLinkType,
): number {
  const magnitude = Math.abs(entryAmount);
  const baseSign = entryDirection === 'credit' ? 1 : -1;
  const reverse = linkType === 'refund' || linkType === 'reversal';
  return reverse ? -baseSign * magnitude : baseSign * magnitude;
}

// ── Payment-status math (pure mirror of recompute_invoice_payment_status) ──
//
// The DB trigger `recompute_invoice_payment_status()` (migration
// 20260504121412_mbank_payment_status_trigger.sql) is the canonical
// writer for invoices.payment_status / paid_amount / paid_date. This
// function is a pure-TypeScript mirror of that math so:
//   - the FE can preview the trigger's verdict without a round-trip
//     (see InvoiceBankPaymentsSection — sum row),
//   - we have a unit-testable codepath for the spec rules (the trigger
//     itself can only be exercised against a real Postgres).
//
// MUST stay in sync with the SQL function. If you change one, change both.

export type InvoicePaymentStatusComputed = 'unpaid' | 'partial' | 'paid' | 'on_hold';

/** Subset of MbankPaymentLinkRow that the math actually reads. */
export interface PaymentStatusLink {
  link_type: MbankPaymentLinkType;
  /** Signed amount in the invoice currency (already conformed by signedLinkAmount). */
  applied_in_invoice_ccy: number;
  /** ISO timestamp; only `payment` rows contribute to paid_date. */
  created_at: string;
}

export interface PaymentStatusInput {
  /** invoice.gross_amount — may be negative for cost invoices. Math uses ABS. */
  gross: number;
  links: PaymentStatusLink[];
  /**
   * Current invoice.payment_status. Only `'on_hold'` is special:
   * preserved unless the math closes to 'paid' (per spec — paid wins).
   */
  priorStatus?: InvoicePaymentStatusComputed | null;
}

export interface PaymentStatusResult {
  status: InvoicePaymentStatusComputed;
  /** ABS(net_settled). null when status is 'unpaid'. */
  paidAmount: number | null;
  /** Latest `payment` link created_at when paid; null otherwise. */
  paidDate: string | null;
  /** SUM(applied_in_invoice_ccy) — sign preserved for callers that want it. */
  netSettled: number;
}

const EPS = 0.01;

/**
 * Compute payment status from the current set of links + invoice gross.
 *
 * Sign convention follows signedLinkAmount() — applied_in_invoice_ccy is
 * stored signed, so net_settled is just the SUM. For status decisions we
 * compare ABS(net_settled) against ABS(gross) because gross itself can be
 * negative (cost invoices).
 *
 * Status rules (ε = 0.01):
 *   - paid    : ABS(ABS(gross) - ABS(net)) <= ε  AND  ABS(gross) > 0
 *   - partial : ABS(net) > ε  AND  not paid
 *   - unpaid  : no links  OR  ABS(net) <= ε
 *   - on_hold : NEVER set by this function; preserved iff prior was on_hold
 *               AND new computed status !== 'paid'
 */
// Pure money helper (no Supabase access), a TS mirror of the recompute_invoice_payment_status DB trigger,
// imported directly by src/entities/invoices/*. Hoisting it into the DAO class would put a stateless
// function behind an instance and churn out-of-scope call sites.
// eslint-disable-next-line no-restricted-syntax -- pure helper, not data access; see note above
export function computePaymentStatus(input: PaymentStatusInput): PaymentStatusResult {
  const grossAbs = Math.abs(input.gross);
  const netSettled = input.links.reduce(
    (acc, l) => acc + (Number.isFinite(l.applied_in_invoice_ccy) ? l.applied_in_invoice_ccy : 0),
    0,
  );
  const netAbs = Math.abs(netSettled);

  let status: InvoicePaymentStatusComputed;
  let paidAmount: number | null;
  let paidDate: string | null;

  if (input.links.length === 0) {
    status = 'unpaid';
    paidAmount = null;
    paidDate = null;
  } else if (grossAbs > 0 && Math.abs(grossAbs - netAbs) <= EPS) {
    // Math closes — paid wins, even over on_hold.
    status = 'paid';
    paidAmount = netAbs;
    paidDate = latestPaymentDate(input.links);
  } else if (netAbs > EPS) {
    status = 'partial';
    paidAmount = netAbs;
    paidDate = null;
  } else {
    // Links exist but cancel out (payment + reversal etc.).
    status = 'unpaid';
    paidAmount = null;
    paidDate = null;
  }

  // Preserve on_hold unless we computed paid.
  if (input.priorStatus === 'on_hold' && status !== 'paid') {
    status = 'on_hold';
  }

  return { status, paidAmount, paidDate, netSettled };
}

function latestPaymentDate(links: PaymentStatusLink[]): string | null {
  let latest: string | null = null;
  for (const l of links) {
    if (l.link_type !== 'payment') continue;
    if (latest == null || l.created_at > latest) latest = l.created_at;
  }
  return latest;
}

export interface TitleRepairInvoice {
  id: number;
  invoice_number: string | null;
  issue_date: string | null;
  seller_company_id: number | null;
  buyer_company_id: number | null;
  gross_amount: number | null;
}
